import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { request } from "utils/request";

export const fetchUserProfile = createAsyncThunk(
  "userProfile/fetchUserProfile",
  async (_, { rejectWithValue }) => {
    try {
      const response = await request(
        `${process.env.REACT_APP_API_URL}/auth/users/me`,
        {
          method: "GET",
        }
      );

      return response;
    } catch (error) {
      return rejectWithValue(
        error || "Failed to fetch user profile. Please try again."
      );
    }
  }
);

export const uploadImage = createAsyncThunk(
  "userProfile/uploadImage",
  async ({ imageData, fieldName }, { rejectWithValue }) => {
    try {
      const response = await request(
        `${process.env.REACT_APP_API_URL}/image/`,
        {
          method: "POST",
          body: imageData, // FormData with image
          headers: {}, // Empty headers - request utility will handle FormData properly
        }
      );

      return { response, fieldName }; // Return both response and field name
    } catch (error) {
      return rejectWithValue({
        error: error || "Failed to upload image. Please try again.",
        fieldName,
      });
    }
  }
);

export const fetchCalendarProviders = createAsyncThunk(
  "userProfile/fetchCalendarProviders",
  async (_, { rejectWithValue }) => {
    try {
      const response = await request(
        `${process.env.REACT_APP_API_URL}/scheduling/providers/`,
        {
          method: "GET",
        }
      );
      return response;
    } catch (error) {
      return rejectWithValue(
        error || "Failed to fetch calendar providers."
      );
    }
  }
);

export const disconnectCalendarProvider = createAsyncThunk(
  "userProfile/disconnectCalendarProvider",
  async (providerId, { rejectWithValue }) => {
    try {
      await request(
        `${process.env.REACT_APP_API_URL}/scheduling/providers/${encodeURIComponent(providerId)}/`,
        {
          method: "DELETE",
        }
      );
      return providerId;
    } catch (error) {
      return rejectWithValue(
        error || "Failed to disconnect calendar provider."
      );
    }
  }
);

export const initiateOAuth = createAsyncThunk(
  "userProfile/initiateOAuth",
  async (provider, { rejectWithValue }) => {
    try {
      const redirectUri = `${window.location.origin}/user-info`;
      const url = new URL(
        `${process.env.REACT_APP_API_URL}/scheduling/oauth/initiate/${encodeURIComponent(provider)}/`
      );
      url.searchParams.set("redirect_uri", redirectUri);
      const response = await request(url.toString(), {
        method: "GET",
      });
      return response;
    } catch (error) {
      return rejectWithValue(
        error || "Failed to initiate calendar connection."
      );
    }
  }
);

export const updateUserProfile = createAsyncThunk(
  "userProfile/updateUserProfile",
  async (profileData, { rejectWithValue }) => {
    try {
      const isFormData = profileData instanceof FormData;

      const response = await request(
        `${process.env.REACT_APP_API_URL}/auth/user/update-profile/`,
        {
          method: "PATCH",
          body: isFormData ? profileData : JSON.stringify(profileData),
          headers: isFormData ? {} : { "Content-Type": "application/json" },
        }
      );
      return response;
    } catch (error) {
      return rejectWithValue(
        error || "Failed to update profile. Please try again."
      );
    }
  }
);

const userProfileSlice = createSlice({
  name: "userProfile",
  initialState: {
    isLoading: false,
    error: null,
    updateSuccess: false,
    data: null,
    profile: null,
    isProfileLoading: false,
    profileError: null,
    isImageUploading: false,
    imageUploadingFields: {},
    imageUploadError: null,
    uploadedImages: {}, // Store multiple uploaded images by field name
    calendarProviders: [],
    isCalendarProvidersLoading: false,
    calendarProvidersError: null,
    isDisconnectingProvider: false,
    disconnectProviderError: null,
    isInitiatingOAuth: false,
    oauthError: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
      state.profileError = null;
      state.imageUploadError = null;
    },
    clearUpdateSuccess: (state) => {
      state.updateSuccess = false;
    },
    clearUploadedImages: (state) => {
      state.uploadedImages = {};
    },
    clearUploadedImage: (state, action) => {
      const fieldName = action.payload;
      if (fieldName && state.uploadedImages[fieldName]) {
        delete state.uploadedImages[fieldName];
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Update Profile reducers
      .addCase(updateUserProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.updateSuccess = false;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.updateSuccess = true;
        state.data = action.payload;
        // Clear uploaded image data after successful profile update
        state.uploadedImages = {};
        // The profile update will be handled by the AppContext
        // when we dispatch the action to update the user profile there
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.updateSuccess = false;
      })
      // Fetch User Profile reducers
      .addCase(fetchUserProfile.pending, (state) => {
        state.isProfileLoading = true;
        state.profileError = null;
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.isProfileLoading = false;
        state.profile = action.payload;
        state.profileError = null;
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.isProfileLoading = false;
        state.profileError = action.payload;
      })
      // Upload Image reducers
      .addCase(uploadImage.pending, (state, action) => {
        const { fieldName } = action.meta.arg;
        state.isImageUploading = true;
        state.imageUploadingFields[fieldName] = true;
        state.imageUploadError = null;
      })
      .addCase(uploadImage.fulfilled, (state, action) => {
        const { response: imageResponse, fieldName } = action.payload;

        state.isImageUploading = false;
        state.imageUploadingFields[fieldName] = false;
        state.imageUploadError = null;

        // Extract the image data from the response (it's a single object, not an array)
        if (imageResponse && imageResponse.id && imageResponse.presigned_url) {
          state.uploadedImages[fieldName] = {
            id: imageResponse.id,
            url: imageResponse.presigned_url,
          };
        } else {
          // Invalid response format - handled by component
        }
      })
      .addCase(uploadImage.rejected, (state, action) => {
        const fieldName =
          action.payload?.fieldName || action.meta?.arg?.fieldName;

        state.isImageUploading = false;
        if (fieldName) {
          state.imageUploadingFields[fieldName] = false;
        }
        state.imageUploadError = action.payload?.error || action.payload;
      })
      // Fetch Calendar Providers
      .addCase(fetchCalendarProviders.pending, (state) => {
        state.isCalendarProvidersLoading = true;
        state.calendarProvidersError = null;
      })
      .addCase(fetchCalendarProviders.fulfilled, (state, action) => {
        state.isCalendarProvidersLoading = false;
        state.calendarProviders = action.payload?.providers || [];
      })
      .addCase(fetchCalendarProviders.rejected, (state, action) => {
        state.isCalendarProvidersLoading = false;
        state.calendarProvidersError = action.payload;
      })
      // Disconnect Calendar Provider
      .addCase(disconnectCalendarProvider.pending, (state) => {
        state.isDisconnectingProvider = true;
      })
      .addCase(disconnectCalendarProvider.fulfilled, (state, action) => {
        state.isDisconnectingProvider = false;
        state.calendarProviders = state.calendarProviders.filter(
          (provider) => provider.id !== action.payload
        );
      })
      .addCase(disconnectCalendarProvider.rejected, (state, action) => {
        state.isDisconnectingProvider = false;
        state.disconnectProviderError = action.payload;
      })
      // Initiate OAuth
      .addCase(initiateOAuth.pending, (state) => {
        state.isInitiatingOAuth = true;
        state.oauthError = null;
      })
      .addCase(initiateOAuth.fulfilled, (state) => {
        state.isInitiatingOAuth = false;
        state.oauthError = null;
      })
      .addCase(initiateOAuth.rejected, (state, action) => {
        state.isInitiatingOAuth = false;
        state.oauthError = action.payload;
      });
  },
});

export const {
  clearError,
  clearUpdateSuccess,
  clearUploadedImages,
  clearUploadedImage,
} = userProfileSlice.actions;
export default userProfileSlice.reducer;
